/* eslint-disable no-unused-vars */
// Add menu state monitoring capabilities
let menuStateCheckInterval = null;
let lastMenuState = null;
let isInitializing = false;
let initializationTimeout = null;
let breadcrumbsInitialized = false;
let breadcrumbRendered = false; // NEW: Track if breadcrumb has been rendered this session

function filterQuickLinks(trail) {
  if (!trail || !Array.isArray(trail)) return trail;

  return trail.filter((entry) => {
    const label = (entry?.label || entry?.title || '').toLowerCase().trim();
    return label !== 'quick links' && label !== 'quicklinks';
  });
}

// Enhanced menu state detection
function getCurrentMenuState() {
  const state = {
    activeElements: [],
    openMenus: [],
    visibleMenus: [],
    menuAttributes: [],
  };

  // Collect active menu elements
  const activeSelectors = ['.header__active-menu-item', '.mega-menu__link--active', '.active', '.current'];
  document.querySelectorAll(activeSelectors.join(', ')).forEach((el) => {
    state.activeElements.push({
      text: el.textContent?.trim(),
      href: el.getAttribute('href'),
      classes: el.className,
    });
  });

  // Collect open/visible menus
  document.querySelectorAll('.mega-menu--open, .mega-menu.is-open, .nav-dropdown--open, .menu-open').forEach((el) => {
    state.openMenus.push({
      text: el.textContent?.trim(),
      classes: el.className,
      id: el.id,
    });
  });

  // Check for menu-related data attributes that might change
  document.querySelectorAll('[data-menu-active], [data-current-menu], [data-selected-menu]').forEach((el) => {
    state.menuAttributes.push({
      element: el.tagName,
      attributes: {
        'data-menu-active': el.getAttribute('data-menu-active'),
        'data-current-menu': el.getAttribute('data-current-menu'),
        'data-selected-menu': el.getAttribute('data-selected-menu'),
      },
    });
  });

  return JSON.stringify(state);
}

// Monitor for menu state changes
function startMenuStateMonitoring() {
  if (menuStateCheckInterval) {
    clearInterval(menuStateCheckInterval);
  }

  lastMenuState = getCurrentMenuState();

  menuStateCheckInterval = setInterval(() => {
    // Don't check if we're already initializing
    if (isInitializing) return;

    const currentMenuState = getCurrentMenuState();
    if (currentMenuState !== lastMenuState) {
      lastMenuState = currentMenuState;
      // Use debounced initialization to prevent multiple rapid calls
      debouncedInitialize();
    }
  }, 500); // Check every 500ms
}

// Stop monitoring when not needed
function stopMenuStateMonitoring() {
  if (menuStateCheckInterval) {
    clearInterval(menuStateCheckInterval);
    menuStateCheckInterval = null;
  }
}

// Debounced initialization to prevent rapid multiple calls
function debouncedInitialize() {
  if (initializationTimeout) {
    clearTimeout(initializationTimeout);
  }

  initializationTimeout = setTimeout(() => {
    if (!isInitializing) {
      initializeBreadcrumbsWithRetry();
    }
  }, 150);
}

function handleize(str) {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// NEW: Get the active menu context from sessionStorage (same as navigation code uses)
function getActiveMenuContext() {
  return {
    activeMenu: sessionStorage.getItem('activeMenu'),
    activeSubMenu: sessionStorage.getItem('activeSubMenu'),
    activeMenuParent: sessionStorage.getItem('activeMenuParent'),
    activeMenuParam: sessionStorage.getItem('activeMenuParam'),
  };
}

// Enhanced initialization with better timing and flicker prevention
function initializeBreadcrumbsWithRetry(maxRetries = 3, currentRetry = 0) {
  // Prevent multiple simultaneous initializations
  if (isInitializing) {
    return;
  }

  // NEW: Skip if breadcrumb already rendered successfully
  if (breadcrumbRendered) {
    return;
  }

  isInitializing = true;

  let trail = findBreadcrumbTrailForCurrentPath();
  let prefix = { label: 'Shop', url: '/' };

  // If no trail found and we haven't exhausted retries, wait for menu updates
  if (!trail && currentRetry < maxRetries) {
    isInitializing = false;
    setTimeout(() => initializeBreadcrumbsWithRetry(maxRetries, currentRetry + 1), (currentRetry + 1) * 200);
    return;
  }

  // Handle product fallback (existing code)
  if (!trail && window.productCollections?.length) {
    let bestCollectionMatch = null;
    let bestScore = -1;
    let isFromStoredContext = false;

    const referrer = document.referrer;
    let referrerCollectionUrl = null;

    if (referrer) {
      try {
        const referrerPath = new URL(referrer).pathname.replace(/\/+$/, '').toLowerCase();
        if (referrerPath.startsWith('/collections/')) {
          referrerCollectionUrl = referrerPath;
        }
      } catch {
        // Invalid referrer URL, ignore
      }
    }

    // First, check if we have stored breadcrumb context for any of the product's collections
    for (const url of window.productCollections) {
      const cleanedCollectionUrl = url.replace(/\/+$/, '').toLowerCase();
      const storedContext = getBreadcrumbContext(cleanedCollectionUrl);

      if (storedContext) {
        bestCollectionMatch = storedContext;
        bestScore = 1000;
        isFromStoredContext = true;
        break;
      }
    }

    // If no stored context, fall back to original matching logic
    if (!bestCollectionMatch) {
      const activeMenuData = findActiveMenuHierarchy();

      for (const url of window.productCollections) {
        const cleanedCollectionUrl = url.replace(/\/+$/, '').toLowerCase();

        for (const path of window.breadcrumbPaths) {
          const last = path[path.length - 1];
          const cleanedLastUrl = last?.url?.replace(/\/+$/, '').toLowerCase();

          if (cleanedLastUrl === cleanedCollectionUrl) {
            let score = 0;

            if (referrerCollectionUrl && cleanedCollectionUrl === referrerCollectionUrl) {
              score += 100;
            }

            const pathLabels = path.map((item) => item.title || item.label);
            for (const activeItem of activeMenuData.activeItems) {
              for (const hierarchyItem of activeItem.hierarchy) {
                if (pathLabels.includes(hierarchyItem)) {
                  score += 20;
                }
              }
            }

            score += path.length;

            if (score > bestScore) {
              bestScore = score;
              bestCollectionMatch = path;
              isFromStoredContext = false;
            }
          }
        }
      }
    }

    if (bestCollectionMatch) {
      const filteredMatch = filterQuickLinks(bestCollectionMatch);

      if (isFromStoredContext) {
        trail = filteredMatch.map((entry) => ({
          label: entry.label,
          url: entry.url,
        }));
      } else {
        trail = filteredMatch.map((entry, index) => ({
          label: entry?.label || entry?.title || 'Missing label',
          url: buildUrlWithMenuParams(entry?.url || '#', filteredMatch, index),
        }));
      }

      trail.push({
        label: window.productTitle,
        url: window.productUrl,
      });
    }
  }

  // Blog and recipe fallback (existing code)
  if (!trail) {
    const path = window.location.pathname.replace(/\/+$/, '').toLowerCase();
    const isRecipe = path.startsWith('/blogs/recipe');
    const isArticle = path.startsWith('/blogs/article');

    if (isRecipe || isArticle) {
      prefix = { label: 'Shop', url: '/' };
      trail = [
        {
          label: isRecipe ? 'Recipes' : 'Blog',
          url: isRecipe ? '/blogs/recipe' : '/blogs/article',
        },
      ];

      const articleTitle = document.querySelector('.article-template__title, .article__title')?.innerText;
      if (articleTitle && path !== trail[0].url) {
        trail.push({ label: articleTitle, url: path });
      }
    } else {
      trail = null;
    }
  }

  if (trail) {
    trail = filterQuickLinks(trail);
  }

  // Only render if we have a trail
  if (trail && trail.length > 0) {
    renderBreadcrumbTrail(trail, prefix);
    breadcrumbRendered = true; // NEW: Mark as rendered
    breadcrumbsInitialized = true;
  }

  storeBreadcrumbContext();
  isInitializing = false;
}

// Enhanced setup function
function setupBreadcrumbs() {
  if (!window.themeNavLinks) {
    // If nav links aren't ready, try again shortly
    setTimeout(setupBreadcrumbs, 500);
    return;
  }

  window.breadcrumbPaths = flattenBreadcrumbPaths(window.themeNavLinks);

  // Start monitoring menu state changes
  startMenuStateMonitoring();

  // Initialize with retry logic - only if not already initialized
  if (!breadcrumbsInitialized) {
    setTimeout(() => initializeBreadcrumbsWithRetry(), 200);
  }
}

// Listen for custom events that indicate menu updates
document.addEventListener('menuUpdated', () => {
  debouncedInitialize();
});

document.addEventListener('navigationUpdated', () => {
  debouncedInitialize();
});

// Listen for storage events (sessionStorage changes from other tabs/windows)
window.addEventListener('storage', (e) => {
  if (
    e.key &&
    (e.key.startsWith('menu_') ||
      e.key.includes('nav') ||
      e.key.includes('breadcrumb') ||
      e.key === 'activeMenu' ||
      e.key === 'activeSubMenu' ||
      e.key === 'activeMenuParent')
  ) {
    debouncedInitialize();
  }
});

// Enhanced URL change detection with debouncing
let lastUrl = location.href;
const urlChangeObserver = new MutationObserver(() => {
  const url = location.href;
  if (url !== lastUrl) {
    lastUrl = url;
    breadcrumbsInitialized = false; // Reset for new page
    breadcrumbRendered = false; // NEW: Reset render flag for new page
    // Reset menu state monitoring for new page
    setTimeout(() => {
      startMenuStateMonitoring();
      initializeBreadcrumbsWithRetry();
    }, 200);
  }
});

// Start observing
urlChangeObserver.observe(document, { subtree: true, childList: true });

// Enhanced event listeners with debouncing
window.addEventListener('popstate', () => {
  breadcrumbsInitialized = false; // Reset for new page
  breadcrumbRendered = false; // NEW: Reset render flag for new page
  setTimeout(() => {
    startMenuStateMonitoring();
    initializeBreadcrumbsWithRetry();
  }, 200);
});

window.addEventListener('hashchange', () => {
  // Hash changes usually don't need breadcrumb updates, but just in case
  debouncedInitialize();
});

// Clean up when page is being unloaded
window.addEventListener('beforeunload', () => {
  stopMenuStateMonitoring();
  if (initializationTimeout) {
    clearTimeout(initializationTimeout);
  }
});

// Your existing functions (keeping them as they are)
function flattenBreadcrumbPaths(nav) {
  const paths = [];

  function walk(node, trail = [], rootIndex = null) {
    const newTrail = [
      ...trail,
      {
        title: node.title,
        url: node.url,
        label: node.label,
        rootIndex: rootIndex,
      },
    ];
    paths.push(newTrail);

    if (node.links?.length) {
      node.links.forEach((child) => walk(child, newTrail, rootIndex));
    }
  }

  nav.forEach((topLevel, index) => {
    walk(topLevel, [], index);
  });

  return paths;
}

function buildUrlWithMenuParams(url, trail, trailIndex) {
  if (!window.themeNavLinks || !trail || trailIndex >= trail.length) {
    return url;
  }

  const rootIndex = trail[0]?.rootIndex;
  if (rootIndex === null || rootIndex >= window.themeNavLinks.length) {
    return url;
  }

  const rootNav = window.themeNavLinks[rootIndex];
  const params = new URLSearchParams();

  const rootHandle = (rootNav.title || rootNav.label || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  if (rootHandle) {
    params.append('menu', rootHandle);
  }

  if (trailIndex > 0 && trail[trailIndex]) {
    const currentItem = trail[trailIndex];
    const currentTitle = currentItem.title || currentItem.label || '';
    const currentHandle = currentTitle
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    if (currentHandle) {
      params.append('submenu', currentHandle);
    }
  }

  const paramString = params.toString();
  return paramString ? `${url}?${paramString}` : url;
}

function storeBreadcrumbContext() {
  const path = window.location.pathname.replace(/\/+$/, '').toLowerCase();

  if (path.startsWith('/collections/')) {
    const trail = findBreadcrumbTrailForCurrentPath();
    if (trail) {
      try {
        // Remove all old breadcrumb_context entries before storing new one
        Object.keys(sessionStorage).forEach((key) => {
          if (key.startsWith('breadcrumb_context_')) {
            sessionStorage.removeItem(key);
          }
        });

        // Store the new breadcrumb context (already filtered)
        sessionStorage.setItem(`breadcrumb_context_${path}`, JSON.stringify(trail));
      } catch {
        // Fallback to global in-memory store
        window.breadcrumbContext = {};
        window.breadcrumbContext[path] = trail;
      }
    }
  }
}

function getBreadcrumbContext(collectionUrl) {
  const cleanedUrl = collectionUrl.replace(/\/+$/, '').toLowerCase();

  try {
    const stored = sessionStorage.getItem(`breadcrumb_context_${cleanedUrl}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Apply filter when retrieving from storage
      return filterQuickLinks(parsed);
    }
  } catch {
    if (window.breadcrumbContext && window.breadcrumbContext[cleanedUrl]) {
      // Apply filter when retrieving from memory
      return filterQuickLinks(window.breadcrumbContext[cleanedUrl]);
    }
  }

  return null;
}

function findActiveMenuHierarchy() {
  const activeMenuData = {
    rootMenu: null,
    activeItems: [],
    menuPath: [],
  };

  const activeSelectors = ['.header__active-menu-item', '.mega-menu__link--active', '.active', '.current'];
  const activeElements = document.querySelectorAll(activeSelectors.join(', '));
  const openMegaMenus = document.querySelectorAll('.mega-menu--open, .mega-menu.is-open, .nav-dropdown--open');
  const allActiveElements = [...activeElements, ...openMegaMenus];

  for (const element of allActiveElements) {
    const menuText = element.textContent?.trim();
    const menuHref = element.getAttribute('href');

    let hierarchy = [];
    let current = element;

    while (current && current !== document.body) {
      const sectionTitle =
        current.getAttribute('data-menu-title') ||
        current.getAttribute('data-section-title') ||
        current.getAttribute('aria-label');

      const menuLink = current.closest('a');
      if (menuLink && menuLink !== element) {
        const linkText = menuLink.textContent?.trim();
        if (linkText && !hierarchy.includes(linkText)) {
          hierarchy.unshift(linkText);
        }
      }

      if (
        current.classList.contains('mega-menu__section') ||
        current.classList.contains('nav-section') ||
        current.classList.contains('menu-section')
      ) {
        const titleElement =
          current.querySelector('.section-title, .menu-title, h3, h4') ||
          current.previousElementSibling?.querySelector('.section-title, .menu-title');

        if (titleElement) {
          const titleText = titleElement.textContent?.trim();
          if (titleText && !hierarchy.includes(titleText)) {
            hierarchy.unshift(titleText);
          }
        }
      }

      if (sectionTitle && !hierarchy.includes(sectionTitle)) {
        hierarchy.unshift(sectionTitle);
      }

      current = current.parentElement;
    }

    if (menuText && !hierarchy.includes(menuText)) {
      hierarchy.push(menuText);
    }

    if (hierarchy.length > 0) {
      activeMenuData.activeItems.push({
        text: menuText,
        href: menuHref,
        hierarchy: hierarchy,
        element: element,
      });
    }
  }

  const secondaryNav = document.querySelector('.secondary-nav:not(.hidden), .sub-nav:not(.hidden)');
  if (secondaryNav) {
    const parentAttr =
      secondaryNav.getAttribute('data-parent') || secondaryNav.querySelector('ul')?.getAttribute('data-parent');
    if (parentAttr) {
      activeMenuData.rootMenu = parentAttr;
    }
  }
  return activeMenuData;
}

// UPDATED: Use sessionStorage context to find the correct trail
function findTrailFromActiveMenu() {
  const activeMenuContext = getActiveMenuContext();
  const activeMenuData = findActiveMenuHierarchy();
  const currentPath = window.location.pathname.replace(/\/+$/, '').toLowerCase();

  const matchingTrails = [];

  for (const trail of window.breadcrumbPaths) {
    for (let itemIndex = 0; itemIndex < trail.length; itemIndex++) {
      const item = trail[itemIndex];
      const itemUrl = item?.url?.replace(/\/+$/, '').toLowerCase();

      if (itemUrl === currentPath) {
        matchingTrails.push({
          trail,
          matchIndex: itemIndex,
          trailLabels: trail.slice(0, itemIndex + 1).map((t) => t.title || t.label),
          trailHandles: trail.slice(0, itemIndex + 1).map((t) => handleize(t.title || t.label)),
          rootIndex: trail[0]?.rootIndex,
        });
      }
    }
  }

  if (matchingTrails.length === 0) {
    return null;
  }

  if (matchingTrails.length === 1) {
    const match = matchingTrails[0];
    return match.trail.slice(0, match.matchIndex + 1);
  }

  let bestMatch = null;
  let bestScore = -1;

  for (const match of matchingTrails) {
    let score = 0;
    const { trail, matchIndex, trailLabels, trailHandles, rootIndex } = match;

    // PRIORITY 1: Match against stored sessionStorage context (highest priority)
    if (activeMenuContext.activeSubMenu) {
      // Check if this trail contains the active submenu
      if (trailHandles.includes(activeMenuContext.activeSubMenu)) {
        score += 100;
      }

      // If we have activeMenuParent, check that too
      if (activeMenuContext.activeMenuParent) {
        if (trailHandles.includes(activeMenuContext.activeMenuParent)) {
          score += 50;
        }
      }
    }

    if (activeMenuContext.activeMenu) {
      // Check if this trail contains the active menu
      if (trailHandles.includes(activeMenuContext.activeMenu)) {
        score += 80;
      }
    }

    // PRIORITY 2: Match against active DOM elements (secondary)
    for (const activeItem of activeMenuData.activeItems) {
      for (const hierarchyItem of activeItem.hierarchy) {
        if (trailLabels.includes(hierarchyItem)) {
          score += 10;
        }
      }

      if (activeItem.text && trailLabels.includes(activeItem.text)) {
        score += 20;
      }
    }

    // PRIORITY 3: Match root menu
    if (activeMenuData.rootMenu && window.themeNavLinks && window.themeNavLinks[rootIndex]) {
      const rootNavTitle = window.themeNavLinks[rootIndex].title || window.themeNavLinks[rootIndex].label;
      const rootHandle = rootNavTitle
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

      if (rootHandle === activeMenuData.rootMenu) {
        score += 30;
      }
    }

    // Prefer shorter trails (tiebreaker)
    score += 10 - matchIndex;

    if (score > bestScore) {
      bestScore = score;
      bestMatch = match;
    }
  }

  if (bestMatch) {
    return bestMatch.trail.slice(0, bestMatch.matchIndex + 1);
  }

  const fallback = matchingTrails[0];
  return fallback.trail.slice(0, fallback.matchIndex + 1);
}

function findBreadcrumbTrailForCurrentPath() {
  const path = window.location.pathname.replace(/\/+$/, '').toLowerCase();

  if (path.startsWith('/blogs/')) {
    return null;
  }

  const trail = findTrailFromActiveMenu();

  if (trail) {
    // Filter QuickLinks from the trail
    const filtered = filterQuickLinks(trail);

    return filtered.map((entry, index) => ({
      label: entry?.label || entry?.title || 'Missing label',
      url: buildUrlWithMenuParams(entry?.url || '#', filtered, index),
    }));
  }
  return null;
}

// Replace the original renderBreadcrumbTrail function with enhanced scroll + fade behavior
function renderBreadcrumbTrail(trail, prefix = null) {
  if (!trail || !trail.length) return;

  // Remove existing breadcrumb if present
  const existingBreadcrumb = document.querySelector('.breadcrumb');
  const target = document.querySelector('[data-breadcrumb-container]');
  if (!target) return;

  // Create nav wrapper
  const nav = document.createElement('nav');
  nav.className = 'breadcrumb';
  nav.setAttribute('aria-label', 'Breadcrumb');
  nav.style.position = 'relative';

  // Create left fade overlay
  const fade = document.createElement('div');
  fade.className = 'breadcrumb__fade';
  fade.setAttribute('aria-hidden', 'true');
  // Add an invisible non-breaking character so global `div:empty { display: none }` won't hide it
  // This is visually inert because CSS uses a background gradient and pointer-events:none
  fade.textContent = '\u00A0';
  nav.appendChild(fade);

  // Create scroll container
  const scrollWrap = document.createElement('div');
  scrollWrap.className = 'breadcrumb__scroll';
  scrollWrap.setAttribute('role', 'navigation');
  scrollWrap.setAttribute('aria-hidden', 'false');

  // Create ordered list
  const list = document.createElement('ol');
  list.className = 'breadcrumb__list';

  if (prefix) {
    const li = document.createElement('li');
    li.className = 'breadcrumb__item';
    const a = document.createElement('a');
    a.className = 'breadcrumb__link';
    a.href = prefix.url;
    a.textContent = prefix.label;
    li.appendChild(a);
    list.appendChild(li);
  }

  trail.forEach((item, i) => {
    const li = document.createElement('li');
    li.className = 'breadcrumb__item';

    if (i === trail.length - 1) {
      li.classList.add('breadcrumb__item--current');
      li.setAttribute('aria-current', 'page');
      const span = document.createElement('span');
      span.className = 'breadcrumb__current';
      span.textContent = item.label;
      li.appendChild(span);
    } else {
      const a = document.createElement('a');
      a.className = 'breadcrumb__link';
      a.href = item.url;
      a.textContent = item.label;
      // ensure navigation on tap remains unchanged (native link behavior)
      li.appendChild(a);
    }

    list.appendChild(li);
  });

  scrollWrap.appendChild(list);
  nav.appendChild(scrollWrap);

  // Atomic replace to prevent flicker
  if (existingBreadcrumb) {
    target.replaceChild(nav, existingBreadcrumb);
  } else {
    // Pre-reserve space by prepending to avoid layout shift
    target.prepend(nav);
  }

  // Scroll & Fade behavior
  let lastScrollLeft = 0;

  let lastTimestamp = Date.now();

  function updateFadeVisibility() {
    const el = scrollWrap;
    const scrolled = el.scrollLeft;
    const maxScroll = el.scrollWidth - el.clientWidth;

    const atLeft = scrolled <= 0;
    const atRight = scrolled >= maxScroll - 1;
    // If there's nothing to scroll, hide fade
    if (maxScroll <= 0) {
      fade.classList.remove('is-visible');
      return;
    }

    // Show fade when content is hidden to the left AND user is not actively scrolling left
    // If we're at rightmost end we want the fade visible as cue there's more left
    if (atRight || scrolled > 5) {
      // Determine scroll direction: if user scrolled left (scrolled < lastScrollLeft) hide fade
      if (scrolled < lastScrollLeft) {
        fade.classList.remove('is-visible');
      } else {
        fade.classList.add('is-visible');
      }
    } else {
      fade.classList.remove('is-visible');
    }

    lastScrollLeft = scrolled;
  }

  // Debounced handler for scroll to ensure smooth updates
  let scrollTimeout = null;
  function onScroll() {
    if (scrollTimeout) clearTimeout(scrollTimeout);
    updateFadeVisibility();
    // If user starts interacting (scrolls left) we should hide the fade immediately
    scrollTimeout = setTimeout(() => {
      updateFadeVisibility();
    }, 80);
  }

  // Attach listeners
  scrollWrap.addEventListener('scroll', onScroll, { passive: true });

  // Also react to touch start so fade hides quickly when user begins an interaction to reveal left items
  scrollWrap.addEventListener(
    'touchstart',
    () => {
      // If user touches and starts moving left we'll detect it via onScroll; ensure fade remains responsive
      // Do a quick update to hide fade if they immediately scroll left
      lastScrollLeft = scrollWrap.scrollLeft;
    },
    { passive: true },
  );

  // On resize or orientation change recalc visibility
  window.addEventListener(
    'resize',
    () => {
      // keep end-aligned behavior when necessary
      updateFadeVisibility();
    },
    { passive: true },
  );

  // Auto-scroll to end on initial render so current page is visible (end-aligned).
  requestAnimationFrame(() => {
    try {
      const max = scrollWrap.scrollWidth - scrollWrap.clientWidth;
      if (max > 0) {
        // Single smooth scroll to end - no instant jump beforehand
        scrollWrap.scrollTo({ left: max, behavior: 'smooth' });
      }
      updateFadeVisibility();
    } catch (e) {
      // fallback - ignore errors silently
    }
  });
}

// Replace the original initializeBreadcrumbs function
function initializeBreadcrumbs() {
  debouncedInitialize();
}

// Initialize everything
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupBreadcrumbs);
} else {
  setupBreadcrumbs();
}
